import { BuildingStId, EquipmentCategory, EquipmentGroupStId, EquipmentStId } from '../../../../types/types'

export type Module = 'behavior' | 'condition' | 'criticality' | 'details' | 'needs' | 'usage' | 'users'
type Category = EquipmentCategory | 'building' | 'group'
type Id = BuildingStId | EquipmentStId | EquipmentGroupStId
type ParentId = BuildingStId | EquipmentGroupStId

interface Error {
  /* Id is either equipmentId, BuildingId, or GroupId */
  id: Id
  /* Parent id is only used for equipment.
   * For them, parentId represents either BuildingId or GroupId
   * This enables us to validate only e.g. elevators for some building */
  parentId: ParentId
  /* Category is high level validation error, e.g. 'escalator' or 'building ' */
  category: Category
  /* Assessment questions are divided into modules or 'sections', e.g. 'details' */
  module: Module
  /* Question key */
  questionKey: string
  /* Explanation string that can be used to show more information about the error */
  explanation: string

  /* For example, following error means:
   *
   *  Elevator with salesToolId 'abc',
   *  that is located in Building with salesToolId 'def',
   *  has problem in 'details' question module,
   *  has problem in 'year' question
   *  and has explanation 'year is mandatory'
   *
   *  {
   *   id: 'abc',
   *   parentId: 'def',
   *   category: 'elevator',
   *   module: 'details',
   *   questionKey: 'year',
   *   explanation: 'Year is mandatory'
   *  }
   */
}

interface ValidationContract {
  id?: Id
  parentId?: ParentId
  category?: Category
  module?: Module
  questionKey?: string
}

export class AssessmentErrors {
  private errors: Error[]

  constructor() {
    this.errors = []
  }

  /* Adds a new validation error. See Error interface for details. */
  addError(e: Error) {
    this.errors.push(e)
  }

  /* Counts error that match all given criteria. Basically, every given
   * parameter acts as a filter. When no params are given, method will
   * return count of this.errors, when parameters are added this.errors
   * gets filtered that narrows down the counting.
   *
   * Idea for the method is to enable querying different parts of offer to find out
   * e.g. 'category "details" contains errors' or how many errors this particular equipment has
   */
  countErrorsBy({ id, parentId, module, category, questionKey }: ValidationContract): number {
    let result = this.errors
    result = this.filterBy(result, 'id', id)
    result = this.filterBy(result, 'parentId', parentId)
    result = this.filterBy(result, 'category', category)
    result = this.filterBy(result, 'module', module)
    result = this.filterBy(result, 'questionKey', questionKey)
    return result.length
  }

  /* Used in similar way like countErrorsBy.
   * Returns true if there are errors matching given parameters.
   * False otherwise
   */
  hasErrorsWith({ id, parentId, module, category, questionKey }: ValidationContract = {}): boolean {
    return this.countErrorsBy({ id, parentId, module, category, questionKey }) > 0
  }

  private filterBy(errors: Error[], key: keyof Error, value?: string): Error[] {
    if (!value) {
      return errors
    }
    return errors.filter(e => e[key] === value)
  }
}
